<template>
  <nav
    class="GlobalNavigation"
    :class="{ 'GlobalNavigation--open': !isClose }"
    @click.stop="onOverlayClick"
  >
    <Card class="GlobalNavigation__card" @click.stop>
      <button
        type="button"
        class="GlobalNavigation__mobileButton"
        :aria-label="isClose ? 'OpenMenu' : 'CloseMenu'"
        @click.stop="toggleClose"
      >
        <Icon :iconName="iconName" class="GlobalNavigation__icon" />
      </button>
      <div class="GlobalNavigation__cardDiv">
        <ul>
          <li v-for="(item, index) in menuItems" :key="index">
            <!-- 単一のページ -->
            <router-link
              v-if="item.type === 'mono'"
              class="GlobalNavigation__mainItem"
              :class="{
                [`GlobalNavigation__mainItem--select`]: isSelected(item.link),
              }"
              :to="item.link"
            >
              <Icon
                class="GlobalNavigation__itemIcon"
                :iconName="item.iconName"
              />
              <span class="GlobalNavigation__heading">{{ item.heading }}</span>
            </router-link>
            <!-- ホーム画面とグループ管理以外 -->
            <button
              v-if="isMenuItemOfOther(item)"
              class="GlobalNavigation__mainItem"
              :class="{
                [`GlobalNavigation__mainItem--childSelect`]:
                  route.path.includes(item.link) && item.isOpen,
              }"
              @click="handleToggle(index)"
            >
              <Icon
                class="GlobalNavigation__itemIcon"
                :iconName="item.iconName"
              />
              <span class="GlobalNavigation__heading">{{ item.heading }}</span>
              <Icon
                class="GlobalNavigation__arrowIcon"
                :class="{
                  [`GlobalNavigation__arrowIcon--up`]: item.isOpen,
                }"
                :iconName="item.isOpen ? 'Up' : 'Down'"
              />
            </button>
            <ul v-if="isMenuItemOfOther(item) && item.isOpen">
              <li v-for="menuDetail in item.details" :key="menuDetail.text">
                <!-- 外部リンクか内部リンクか判定してrouter-linkかaタグに出し分け -->
                <component
                  :is="isInternalLink(menuDetail.link) ? 'router-link' : 'a'"
                  :to="isInternalLink(menuDetail.link) ? menuDetail.link : ''"
                  :href="isInternalLink(menuDetail.link) ? '' : menuDetail.link"
                  :target="isInternalLink(menuDetail.link) ? '' : '_blank'"
                  class="GlobalNavigation__subItem"
                  :class="{
                    [`GlobalNavigation__subItem--select`]:
                      $route.path === menuDetail.link,
                  }"
                >
                  {{ menuDetail.text }}
                </component>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </Card>
  </nav>
</template>

<script lang="ts">
export default {
  name: 'GlobalNavigation',
};
</script>

<script lang="ts" setup>
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import { Ref, computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const menuItemType = {
  mono: 'mono',
  other: 'other',
} as const;

type MenuItem = MenuItemOfMono | MenuItemOfOther;

type MenuItemType = typeof menuItemType;

type MenuItemOfMono = {
  type: MenuItemType['mono'];
  iconName: string;
  heading: string;
  link: string;
};

type MenuItemOfOther = {
  type: MenuItemType['other'];
  iconName: string;
  heading: string;
  link: string;
  isOpen: boolean;
  details: MenuItemDetail[];
};

type MenuItemDetail = {
  link: string;
  text: string;
};

interface Props {
  globalMenuItems: MenuItem[];
}

const props = defineProps<Props>();

const menuItems: Ref<MenuItem[]> = ref(props.globalMenuItems ?? []);

watch(
  () => props.globalMenuItems,
  newItems => {
    if (newItems) {
      menuItems.value = newItems;
    }
  },
  { immediate: true }
);

const isClose = ref(true);
const toggleClose = () => (isClose.value = !isClose.value);
const iconName = computed(() => (isClose.value ? 'DoubleRight' : 'DoubleLeft'));

//Click on mobile overlay to close
const onOverlayClick = () => {
  if (!isClose.value) {
    toggleClose();
  }
};

// 現在ページのメニューを開いた状態にする
const isMenuItemOfOther = (item: MenuItem): item is MenuItemOfOther => {
  return item.type === 'other';
};
const openMenuItem = () => {
  const currentPageMenuItem = menuItems.value
    ?.filter(isMenuItemOfOther)
    .find(item => {
      for (const detail of item.details) {
        if (route.path.includes(detail.link)) {
          return true;
        }
      }
      return false;
    });
  if (currentPageMenuItem) {
    currentPageMenuItem.isOpen = true;
  }
};
openMenuItem();
//watch(contractDetails, () => openMenuItem());

const handleToggle = (index: number) => {
  const item = menuItems.value[index];
  if (!item) return;

  if (isMenuItemOfOther(item)) {
    item.isOpen = !item.isOpen;
  }
};

// 外部リンクか内部リンクか判定し、外部リンクならfalseを返す
const isInternalLink = (path: string) => {
  return !/^https?:\/\//.test(path);
};

const isSelected = (link: string) => {
  if (link === '/') {
    return route.path === '/' || route.path.includes('/licenses');
  }
  return route.path.startsWith(link);
};
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.GlobalNavigation {
  height: 100%;
  background-color: $color-white-100;
  box-shadow: $service-shadow-mp;

  & &__card {
    position: relative;
    width: $service-unit * 60;
    height: 100%;
  }

  &__cardDiv {
    height: 100%;
  }

  &__mobileButton {
    display: none;
  }

  &__icon {
    display: none;
  }

  &__mainItem {
    display: flex;
    align-items: center;
    width: 100%;
    padding: $service-unit * 2 $service-unit * 4;
    font-size: $service-font-size-mp;
    font-weight: bold;
    color: $color-gray-80;
    cursor: pointer;
    background-color: $color-white-100;

    &:hover {
      text-decoration: none;
      background-color: $color-gray-20;
    }

    &--select {
      padding: $service-unit * 2 $service-unit * 4 $service-unit * 2
        $service-unit * 3;
      pointer-events: none;
      background-color: $color-gray-20;
      border-left: $service-unit solid $color-blue-60;
    }

    &--childSelect {
      pointer-events: none;
    }
  }

  &__heading {
    margin-left: $service-unit * 2;
  }

  &__itemIcon,
  & &__arrowIcon {
    flex-shrink: 0;
    width: $service-unit * 6;
    height: $service-unit * 6;
  }
  & &__arrowIcon {
    margin-left: auto;
    transition: 0.2s ease-out;

    &--up {
      transform: rotate(180deg);
    }
  }

  &__subItem {
    display: block;
    padding: $service-unit * 2 $service-unit * 4 $service-unit * 2 $service-unit *
      12;
    font-size: $service-font-size-mp;
    color: $color-gray-80;
    background-color: $color-white-100;

    &:hover {
      text-decoration: none;
      background-color: $color-gray-20;
    }

    &--select {
      padding: $service-unit * 2 $service-unit * 4 $service-unit * 2
        $service-unit * 11;
      font-weight: bold;
      pointer-events: none;
      background-color: $color-gray-20;
      border-left: $service-unit solid $color-blue-60;
    }
  }

  @media (max-width: 767px) {
    &--open {
      position: absolute;
      z-index: $service-z-index-ff;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border: none;
      background-color: $color-background-shadow;
    }

    &--open &__card {
      display: flex;
      flex-direction: column;
    }

    &:not(&--open) &__card {
      width: $service-unit * 6;
    }

    &__mobileButton {
      display: flex;
      justify-content: end;
      width: 100%;
      height: 100%;
      background-color: $color-white-100;
    }

    &--open &__mobileButton {
      display: flex;
      justify-content: end;
      align-items: center;
      height: unset;
      padding: 0 $service-unit * 2 $service-unit * 2;
      z-index: $service-z-index-ff + 1; // Make sure it stays above overlay
    }

    &__icon {
      display: block;
      width: $service-unit * 6;
      height: $service-unit * 6;
      z-index: $service-z-index-ff + 1;
    }

    &:not(&--open) &__scrollBox {
      display: none;
    }
  }
}
</style>
